/**
 * Содержимое карточки отсчёта: что известно про точку под курсором.
 *
 * Строки собираются здесь, а не в компоненте, по тем же причинам, что у
 * карточки землетрясения: «что показать» — логика и проверяется тестом, «как
 * это звучит» — каталог перевода. Числа идут через `Intl`, иначе в русском
 * кабинете вылезла бы десятичная точка.
 *
 * ПОРЯДОК СТРОК ЗАДАЁТ СЕРВЕР (`PROBE_ORDER`), и мы его не пересобираем: он
 * один на обе витрины и на будущий экспорт, а второй порядок здесь означал бы
 * две разные карточки на одни данные.
 *
 * РАЗРЯДНОСТЬ — ПО СЛОЮ, А НЕ ПО ЕДИНИЦЕ. У аномалии уровня моря и у глубины
 * дна единица одна (метры), но сантиметр аномалии — сигнал, а сантиметр дна —
 * шум эхолота: «-34,60 м» под килем читается как ложная точность.
 */
import { formatNumber, layerLabel, t, unitLabel, type Lang } from '../i18n'
import { districtLine, type DistrictAnswer } from './districts'
import { banLines, type BanAnswer } from './bans'
import { zoneLine, type ZoneAnswer } from './eez'

export interface PointValue {
  id: string
  value: number | null
  unit: string
  /** Горизонт значения; null — величина поверхностная (ветер, уровень, дно). */
  depth: number | null
  /** Куда течёт/дует, градусы от севера по часовой. Только у векторных слоёв. */
  direction?: number | null
}

export interface PointNode {
  id: number
  lat: number
  lon: number
  distance_km: number
}

export interface PointProbe {
  lat: number
  lon: number
  date: string
  depth: number
  node: PointNode | null
  values: PointValue[]
  /** В чьей зоне точка. Поля может не быть вовсе: витрина и бэкенд
   *  выкатываются порознь, и ответ без него обязан нарисоваться. */
  zone?: ZoneAnswer | null
  district?: DistrictAnswer | null
  bans?: BanAnswer | null
}

export interface PointRow {
  id: string
  label: string
  /** Готовая строка значения с единицей, либо слова «нет данных». */
  value: string
  /** Значения нет — компонент гасит строку, а не прячет её. */
  empty: boolean
  /** Величина поверхностная, а карточка смотрит вглубь: нужна пометка. */
  surface: boolean
}

export interface PointCardView {
  coords: string
  node: { coords: string; distance: string } | null
  rows: PointRow[]
  /** Готовая строка «в чьей зоне точка»; `null` — данных о зонах нет.
   *
   *  Строкой, а не рядом в `rows`: у зоны нет ни единицы, ни горизонта, и
   *  «нет данных» на её месте означало бы совсем другое, чем у измерения. */
  zone: string | null
  /** «В каком промысловом районе» — ДРУГОЙ вопрос, чем «в чьей воде»: рубеж
   *  ИЭЗ говорит про право государства, район — про правила лова. Своей
   *  строкой, а не припиской к зоне: их путают и без нашей помощи. */
  district: string | null
  /** Запреты под точкой на дату карточки и для её вида; `null` — не знаем
   *  (сюда не смотрели). Список, а не строка: у точки их бывает несколько —
   *  полоса острова плюс запрет вида плюс тралы на мелководье. */
  bans: string[] | null
}

/**
 * Сколько знаков после запятой держать у слоя.
 *
 * Числа выбраны по РАЗРЕШАЮЩЕЙ СПОСОБНОСТИ источника, а не по красоте: лишний
 * знак — обещание точности, которой у продукта нет.
 */
const DIGITS: Record<string, number> = {
  probability: 0, // оценка в процентах; десятые доли пригодности не значат ничего
  upwelling: 2, // безразмерный индекс, весь диапазон в пределах единицы
  sst: 1, // точность продукта CMEMS ~0.1 °C
  salinity: 2,
  oxygen: 0, // ммоль/м³ — величина в сотнях
  chlorophyll: 2, // мг/м³ — величина в единицах и долях
  zooplankton: 1,
  currents: 2, // м/с, разрешение модели ~0.01
  ssha: 2, // аномалия уровня в сантиметрах и есть весь сигнал
  wind_speed: 1,
  bathymetry: 0, // метры дна; сантиметры здесь — шум эхолота
}

/** Разрядность по умолчанию — на слой, которого витрина ещё не знает. */
const DEFAULT_DIGITS = 2

/** Знаков в координате: 4 ≈ 11 м, вчетверо мельче самой густой нашей сетки. */
const COORD_DIGITS = 4

/**
 * Долгота карты → та, что читают с прибора.
 *
 * Район у нас непрерывен на восток, и MapLibre за 180-м отдаёт 185.4° либо
 * −174.6° в зависимости от того, сколько раз карту прокрутили. На компасе не
 * бывает ни того, ни другого: приводим к знаковой форме [−180, 180).
 */
export function signedLon(lon: number): number {
  return ((((lon + 180) % 360) + 360) % 360) - 180
}

/** «57,6987° с.ш.  23,7696° в.д.» — широта первой, как в судовождении. */
export function coordText(lat: number, lon: number, lang: Lang): string {
  const lonSigned = signedLon(lon)
  const ns = t(lat >= 0 ? 'maps.point.north' : 'maps.point.south')
  const ew = t(lonSigned >= 0 ? 'maps.point.east' : 'maps.point.west')
  const latText = `${formatNumber(lang, Math.abs(lat), COORD_DIGITS)}° ${ns}`
  const lonText = `${formatNumber(lang, Math.abs(lonSigned), COORD_DIGITS)}° ${ew}`
  return `${latText}  ${lonText}`
}

function valueText(item: PointValue, lang: Lang): string {
  const digits = DIGITS[item.id] ?? DEFAULT_DIGITS
  const unit = unitLabel(item.unit)
  const head = `${formatNumber(lang, item.value, digits)}${unit ? ` ${unit}` : ''}`
  if (item.direction === null || item.direction === undefined) return head
  // Направление приклеивается к величине, а не живёт отдельной строкой: «куда»
  // без «сколько» — не показание, а половина его.
  return `${head} · ${formatNumber(lang, Math.round(item.direction), 0)}°`
}

export function pointCardView(probe: PointProbe, lang: Lang): PointCardView {
  const rows: PointRow[] = probe.values.map(item => ({
    id: item.id,
    label: layerLabel(item.id),
    value: item.value === null ? t('maps.point.noData') : valueText(item, lang),
    empty: item.value === null,
    // На нулевом горизонте помечать нечего: там поверхностно всё.
    surface: item.depth === null && probe.depth !== 0,
  }))

  return {
    coords: coordText(probe.lat, probe.lon, lang),
    node: probe.node
      ? {
          coords: coordText(probe.node.lat, probe.node.lon, lang),
          distance: `${formatNumber(lang, probe.node.distance_km, 1)} ${t('units.km')}`,
        }
      : null,
    rows,
    zone: zoneLine(probe.zone),
    district: districtLine(probe.district, t),
    bans: banLines(probe.bans, t),
  }
}
